import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Modal,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Container,
  Divider
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  FaEye,
  FaSearch,
  FaUser,
  FaTasks,
  FaUserInjured,
  FaDatabase,
  FaBriefcase,
  FaPills,
  FaTools,
  FaLock
} from 'react-icons/fa';
import { useForm, Controller } from 'react-hook-form';
import {
  fetchIncidents,
  clearIncidentsState,
  ClientIncident,
  DataBreach,
  EmployeeIncident,
  MedicationIncident,
  EquipmentIncident,
  RestrictivePractices
} from 'slices/NdisSlices/carporateServices/IncidentsListSlice';
import { fetchParticipantsList } from 'slices/HomeTilesSlices';
import { AppDispatch, RootState } from 'app/store';
import ClientIncidentForm from '../../reportAllForms/forms/ClientIncidentForm';
import DataBreachIncidentForm from '../../reportAllForms/forms/DataBreachIncidentForm';
import EmployeeIncidentReportForm from '../../reportAllForms/forms/EmployeeIncidentForm';
import MedicationIncidentReportForm from '../../reportAllForms/forms/MedicationIncidentReportForm';
import EquipmentIncidentReportForm from '../../reportAllForms/forms/EquipmentIncidentReportForm';
import RestrictivePracticesReportForm from '../../reportAllForms/forms/RestrictivePracticesReportForm';

interface FilterFormData {
  client_email: string;
  search: string;
}

type IncidentType = 'client' | 'data' | 'employee' | 'medication' | 'equipment' | 'restrictive';

interface TabConfig {
  label: string;
  value: IncidentType;
  icon: JSX.Element;
}

const IncidentsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();

  const [activeTab, setActiveTab] = useState<IncidentType>('client');
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openFollowupModal, setOpenFollowupModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<
    | ClientIncident
    | DataBreach
    | EmployeeIncident
    | MedicationIncident
    | EquipmentIncident
    | RestrictivePractices
    | null
  >(null);
  const [isClientsLoading, setIsClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');

  // Define tabs array
  const tabs: TabConfig[] = [
    {
      label: 'Client',
      value: 'client',
      icon: <FaUserInjured size={16} />
    },
    {
      label: 'Data',
      value: 'data',
      icon: <FaDatabase size={16} />
    },
    {
      label: 'Employee',
      value: 'employee',
      icon: <FaBriefcase size={16} />
    },
    {
      label: 'Medication',
      value: 'medication',
      icon: <FaPills size={16} />
    },
    {
      label: 'Equipment',
      value: 'equipment',
      icon: <FaTools size={16} />
    },
    {
      label: 'Restrictive',
      value: 'restrictive',
      icon: <FaLock size={16} />
    }
  ];

  // Get user details and project/service selections
  const loginInfo = localStorage.getItem('user-details');
  const userDetails = loginInfo ? JSON.parse(loginInfo) : null;
  const selectedProjectId = useSelector(
    (state: RootState) => state.homeTilesServices.selectedProjectId
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected.selectedServiceTitle
  );
  const email = userDetails?.email || '';
  const role = userDetails?.role ? [userDetails.role] : [''];

  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  const isAdminLogin =
    selectedProject?.isAdmin === true || role?.some((r: any) => r?.label?.toLowerCase() === 'admin');

  // Fetch clients
  useEffect(() => {
    if (selectedProjectId && selectedServiceTitle && email && role.length > 0) {
      setIsClientsLoading(true);
      dispatch(
        fetchParticipantsList({
          group_id: selectedProjectId || '',
          service_name_id: selectedServiceTitle || '',
          nested_component_id: pathname,
          email: isAdminLogin? '' : email,
          roles: role,
          client_name: ''
        })
      )
        .unwrap()
        .then(() => {
          setIsClientsLoading(false);
        })
        .catch((error) => {
          setIsClientsLoading(false);
          setClientsError('Failed to load clients. Please try again later.');
        });
    } else {
      setIsClientsLoading(false);
    }
  }, [selectedProjectId, selectedServiceTitle]);

  // Get incidents data
  const {
    client_incident,
    data_breach,
    employee_incident,
    medication_incident,
    equipment_incident,
    restrictive_practices,
    loading,
    error
  } = useSelector((state: RootState) => state.incidentsList);

  const participantsList = useSelector(
    (state: RootState) => state?.homeTilesServices?.participants
  ) || { participants_list: [] };

  // Prepare clients list
  const clientsList = useMemo(
    () =>
      participantsList.participants_list?.map(
        (participant: { name: string; email: string }, index: number) => ({
          label: participant.name || `Client ${index + 1}`,
          id: index + 1,
          email: participant.email
        })
      ) || [],
    [participantsList.participants_list]
  );

  // Get default client from localStorage
  const selectedClient = JSON.parse(localStorage.getItem('selectedClient') || '{}') as {
    email?: string;
  };
  const defaultClientEmail = selectedClient.email || '';

  // Form handling
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: { client_email: defaultClientEmail, search: '' }
  });

  const clientEmail = watch('client_email');

  // Fetch incidents data when client changes
  useEffect(() => {
    if (clientEmail || defaultClientEmail) {
      dispatch(fetchIncidents({ client_email: clientEmail || defaultClientEmail }));
    }
    return () => {
      dispatch(clearIncidentsState());
    };
  }, [dispatch, clientEmail, defaultClientEmail]);

  // Filter data function
  const filterData = <T extends { [key: string]: any }>(data: T[], fields: string[]): T[] => {
    const searchTerm = watch('search');
    if (!searchTerm) return data;
    const lowerSearch = searchTerm.toLowerCase();
    return data.filter((item) =>
      fields.some((field) => item[field]?.toString().toLowerCase().includes(lowerSearch))
    );
  };

  // Filtered data sets
  const filteredClientIncidents = filterData<ClientIncident>(client_incident, [
    'client_name',
    'description',
    'location',
    'client_condition'
  ]);

  const filteredDataBreaches = filterData<DataBreach>(data_breach, [
    'type_of_data',
    'description',
    'individuals_affected'
  ]);

  const filteredEmployeeIncidents = filterData<EmployeeIncident>(employee_incident, [
    'employee_name',
    'description',
    'job_title'
  ]);

  const filteredMedicationIncidents = filterData<MedicationIncident>(medication_incident, [
    'client_name',
    'nature_of_incident',
    'description'
  ]);

  const filteredEquipmentIncidents = filterData<EquipmentIncident>(equipment_incident, [
    'incident_type',
    'description',
    'reporter_name'
  ]);

  const filteredRestrictivePractices = filterData<RestrictivePractices>(restrictive_practices, [
    'client_name',
    'type_of_practice',
    'description'
  ]);

  // View and Followup handlers
  const handleView = (
    item:
      | ClientIncident
      | DataBreach
      | EmployeeIncident
      | MedicationIncident
      | EquipmentIncident
      | RestrictivePractices
  ) => {
    setSelectedItem(item);
    setOpenViewModal(true);
  };

  const handleFollowup = (
    item:
      | ClientIncident
      | DataBreach
      | EmployeeIncident
      | MedicationIncident
      | EquipmentIncident
      | RestrictivePractices
  ) => {
    setSelectedItem(item);
    setOpenFollowupModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedItem(null);
  };

  const handleCloseFollowupModal = () => {
    setOpenFollowupModal(false);
    setSelectedItem(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: IncidentType) => {
    setActiveTab(newValue);
    setSelectedItem(null);
  };

  // Form renderer for followup modal
  const renderFollowupForm = () => {
    if (!selectedItem) return null;

    switch (activeTab) {
      case 'client':
        return (
          <ClientIncidentForm
            initialData={selectedItem as ClientIncident}
            onClose={handleCloseFollowupModal}
          />
        );
      case 'data':
        return (
          <DataBreachIncidentForm
            initialData={selectedItem as DataBreach}
            onClose={handleCloseFollowupModal}
          />
        );
      case 'employee':
        return (
          <EmployeeIncidentReportForm
            initialData={selectedItem as EmployeeIncident}
            onClose={handleCloseFollowupModal}
          />
        );
      case 'medication':
        return (
          <MedicationIncidentReportForm
            initialData={selectedItem as MedicationIncident}
            onClose={handleCloseFollowupModal}
          />
        );
      case 'equipment':
        return (
          <EquipmentIncidentReportForm
            initialData={selectedItem as EquipmentIncident}
            onClose={handleCloseFollowupModal}
          />
        );
      case 'restrictive':
        return (
          <RestrictivePracticesReportForm
            initialData={selectedItem as RestrictivePractices}
            onClose={handleCloseFollowupModal}
          />
        );
      default:
        return null;
    }
  };

  // Detail view renderer with improved label-value format
  const renderDetails = (
    item:
      | ClientIncident
      | DataBreach
      | EmployeeIncident
      | MedicationIncident
      | EquipmentIncident
      | RestrictivePractices
  ) => {
    const formatLabel = (label: string) => {
      return label
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const formatValue = (value: any) => {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      return value.toString();
    };

    const getCategoryColor = () => {
      switch (activeTab) {
        case 'client':
          return theme.palette.warning.light;
        case 'data':
          return theme.palette.error.light;
        case 'employee':
          return theme.palette.info.light;
        case 'medication':
          return theme.palette.success.light;
        case 'equipment':
          return theme.palette.primary.light;
        case 'restrictive':
          return theme.palette.secondary.light;
        default:
          return theme.palette.grey[300];
      }
    };

    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            p: 2,
            backgroundColor: getCategoryColor(),
            borderRadius: 1
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {activeTab === 'client'
              ? 'Client Incident Details'
              : activeTab === 'data'
              ? 'Data Breach Details'
              : activeTab === 'employee'
              ? 'Employee Incident Details'
              : activeTab === 'medication'
              ? 'Medication Incident Details'
              : activeTab === 'equipment'
              ? 'Equipment Incident Details'
              : 'Restrictive Practice Details'}
          </Typography>
          <IconButton onClick={handleCloseViewModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Grid container spacing={1.5}>
          {Object.entries(item).map(([key, value]) => (
            <React.Fragment key={key}>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatLabel(key)}
                  </Typography>
                  {key === 'photo' &&
                  typeof value === 'string' &&
                  value.startsWith('data:image') ? (
                    <Box
                      component="img"
                      src={value}
                      alt="Incident"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 150,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        objectFit: 'contain',
                        mt: 1
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {formatValue(value)}
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ my: 1 }} />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    );
  };

  // Card renderer
  const renderCard = (
    item:
      | ClientIncident
      | DataBreach
      | EmployeeIncident
      | MedicationIncident
      | EquipmentIncident
      | RestrictivePractices
  ) => {
    const getBorderColor = () => {
      switch (activeTab) {
        case 'client':
          return theme.palette.warning.light;
        case 'data':
          return theme.palette.error.light;
        case 'employee':
          return theme.palette.info.light;
        case 'medication':
          return theme.palette.success.light;
        case 'equipment':
          return theme.palette.primary.light;
        case 'restrictive':
          return theme.palette.secondary.light;
        default:
          return theme.palette.grey[300];
      }
    };

    const getCardContent = () => {
      switch (activeTab) {
        case 'client':
          const clientIncident = item as ClientIncident;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {clientIncident.client_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Location: {clientIncident.location}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {clientIncident.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {clientIncident.date}
                </Typography>
                <Typography
                  variant="caption"
                  color={clientIncident.property_damage ? 'error.main' : 'text.secondary'}
                >
                  {clientIncident.property_damage ? 'Damage' : 'No Damage'}
                </Typography>
              </Box>
            </>
          );

        case 'data':
          const dataBreach = item as DataBreach;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {dataBreach.reporter_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Type: {dataBreach.type_of_data}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {dataBreach.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {dataBreach.date}
                </Typography>
                <Typography
                  variant="caption"
                  color={dataBreach.breach_reported_externally ? 'success.main' : 'warning.main'}
                >
                  {dataBreach.breach_reported_externally ? 'Reported' : 'Not Reported'}
                </Typography>
              </Box>
            </>
          );

        case 'employee':
          const employeeIncident = item as EmployeeIncident;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {employeeIncident.employee_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {employeeIncident.job_title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {employeeIncident.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {employeeIncident.date}
              </Typography>
            </>
          );

        case 'medication':
          const medicationIncident = item as MedicationIncident;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {medicationIncident.client_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {medicationIncident.nature_of_incident}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {medicationIncident.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {medicationIncident.date}
                </Typography>
                <Typography
                  variant="caption"
                  color={
                    medicationIncident.reported_to_healthcare ? 'success.main' : 'warning.main'
                  }
                >
                  {medicationIncident.reported_to_healthcare ? 'Reported' : 'Not Reported'}
                </Typography>
              </Box>
            </>
          );

        case 'equipment':
          const equipmentIncident = item as EquipmentIncident;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {equipmentIncident.reporter_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {equipmentIncident.incident_type}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {equipmentIncident.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {equipmentIncident.date}
                </Typography>
                <Typography
                  variant="caption"
                  color={equipmentIncident.was_injury ? 'error.main' : 'success.main'}
                >
                  {equipmentIncident.was_injury ? 'Injury' : 'No Injury'}
                </Typography>
              </Box>
            </>
          );

        case 'restrictive':
          const restrictivePractice = item as RestrictivePractices;
          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {restrictivePractice.client_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {restrictivePractice.type_of_practice}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem'
                }}
              >
                {restrictivePractice.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {restrictivePractice.date}
                </Typography>
                <Typography
                  variant="caption"
                  color={restrictivePractice.approved_in_plan ? 'success.main' : 'warning.main'}
                >
                  {restrictivePractice.approved_in_plan ? 'Approved' : 'Not Approved'}
                </Typography>
              </Box>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 1,
          borderLeft: `4px solid ${getBorderColor()}`,
          '&:hover': {
            boxShadow: 3
          },
          transition: 'all 0.2s ease'
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 2 }}>{getCardContent()}</CardContent>
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            size="small"
            onClick={() => handleFollowup(item)}
            startIcon={<FaTasks size={14} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              p: '4px 8px'
            }}
          >
            Followup
          </Button>
          <Button
            size="small"
            onClick={() => handleView(item)}
            startIcon={<FaEye size={14} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              p: '4px 8px'
            }}
          >
            Details
          </Button>
        </Box>
      </Card>
    );
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'client':
        return filteredClientIncidents;
      case 'data':
        return filteredDataBreaches;
      case 'employee':
        return filteredEmployeeIncidents;
      case 'medication':
        return filteredMedicationIncidents;
      case 'equipment':
        return filteredEquipmentIncidents;
      case 'restrictive':
        return filteredRestrictivePractices;
      default:
        return [];
    }
  };

  const currentData = getCurrentData();

  return (
    <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1, sm: 2 }, pb: isMobile ? 8 : 2 }}>
      {/* Filters */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 2,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center'
        }}
      >
        <Controller
          name="client_email"
          control={control}
          render={({ field }) => (
            <Select
              size="small"
              value={field.value}
              onChange={(e: SelectChangeEvent<string>) => field.onChange(e.target.value)}
              displayEmpty
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? '100%' : 250 }}
              inputProps={{ 'aria-label': 'Select client' }}
              startAdornment={
                <InputAdornment position="start">
                  <FaUser size={14} />
                </InputAdornment>
              }
              disabled={isClientsLoading}
            >
              <MenuItem value="">All Clients</MenuItem>
              {clientsList.map((client) => (
                <MenuItem key={client.id} value={client.email}>
                  {client.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        <Controller
          name="search"
          control={control}
          render={({ field }) => (
            <TextField
              size="small"
              placeholder="Search..."
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch size={14} />
                  </InputAdornment>
                )
              }}
              {...field}
            />
          )}
        />
      </Box>

      {/* Error Messages */}
      {clientsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClientsError('')}>
          {clientsError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearIncidentsState())}>
          {error}
        </Alert>
      )}

      {/* Loading States */}
      {(isClientsLoading || loading) && (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
        >
          <CircularProgress size={isMobile ? 24 : 40} />
        </Box>
      )}

      {/* Main Content */}
      {!(isClientsLoading || loading) && (
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: 2 }}>
          {/* Desktop Tabs */}
          {!isMobile && (
            <Paper
              sx={{
                width: 200,
                p: 1.5,
                height: 'fit-content',
                position: 'sticky',
                top: 80,
                borderRadius: 1.5
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Categories
              </Typography>
              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    py: 1,
                    px: 1.5,
                    minHeight: 'auto'
                  },
                  '& .Mui-selected': {
                    bgcolor: theme.palette.action.selected,
                    color: theme.palette.primary.main,
                    borderRadius: 1
                  }
                }}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                    icon={tab.icon}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Paper>
          )}

          {/* Tab Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {tabs.find((tab) => tab.value === activeTab)?.label || 'Incidents'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentData.length} {currentData.length === 1 ? 'record' : 'records'}
              </Typography>
            </Box>

            {currentData.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2">No records found for the selected filters</Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {currentData.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    {renderCard(item)}
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      )}

      {/* Mobile Bottom Tabs */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              width: '100%',
              '& .MuiTab-root': {
                minWidth: 'unset',
                minHeight: 56,
                fontSize: '0.75rem',
                px: 1.5
              }
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                icon={tab.icon}
                iconPosition="top"
              />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* View Details Modal */}
      <Modal
        open={openViewModal}
        onClose={handleCloseViewModal}
        aria-labelledby="view-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <Paper
          sx={{
            width: '100%',
            maxWidth: { xs: '95%', sm: 800 },
            maxHeight: '90vh',
            overflowY: 'auto',
            borderRadius: 1.5,
            position: 'relative'
          }}
        >
          {selectedItem && renderDetails(selectedItem)}
        </Paper>
      </Modal>

      {/* Followup Modal */}
      <Modal
        open={openFollowupModal}
        onClose={handleCloseFollowupModal}
        aria-labelledby="followup-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '95%', sm: 800 },
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 1.5,
            boxShadow: 24
          }}
        >
          <IconButton
            onClick={handleCloseFollowupModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: 'background.default',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {renderFollowupForm()}
        </Box>
      </Modal>
    </Container>
  );
};

export default IncidentsList;
